/**
 * 数据变化检测 Hook
 * 用于检测数据是否发生变化
 */
import { useState, useEffect, useRef } from 'react';

export interface DataChangeDetectorConfig<T> {
  data: T;
  enabled?: boolean;
  compareFunction?: (prev: T, current: T) => boolean;
  onDataChange?: (prev: T, current: T) => void;
}

export interface DataChangeDetectorReturn {
  hasChanges: boolean;
  changeCount: number;
  acknowledgeChanges: () => void;
  resetChangeDetection: () => void;
}

/**
 * 默认比较函数 - 使用 JSON 序列化比较
 */
const defaultCompare = <T,>(prev: T, current: T): boolean => {
  try {
    return JSON.stringify(prev) !== JSON.stringify(current);
  } catch {
    return prev !== current;
  }
};

/**
 * 数据变化检测 Hook
 */
export const useDataChangeDetector = <T,>({
  data,
  enabled = true,
  compareFunction = defaultCompare,
  onDataChange
}: DataChangeDetectorConfig<T>): DataChangeDetectorReturn => {
  const [hasChanges, setHasChanges] = useState(false);
  const [changeCount, setChangeCount] = useState(0);
  const previousDataRef = useRef<T>(data);
  const acknowledgedDataRef = useRef<T>(data);

  useEffect(() => {
    if (!enabled) return;

    // 检测数据是否变化
    if (compareFunction(previousDataRef.current, data)) {
      // 数据发生了变化
      if (!compareFunction(acknowledgedDataRef.current, data)) {
        // 这是新的变化（用户还没有确认过）
        setHasChanges(true);
        setChangeCount(prev => prev + 1);

        // 触发回调
        if (onDataChange) {
          onDataChange(previousDataRef.current, data);
        }
      }

      previousDataRef.current = data;
    }
  }, [data, enabled, compareFunction, onDataChange]);

  // 确认变化（清除提示）
  const acknowledgeChanges = () => {
    setHasChanges(false);
    acknowledgedDataRef.current = data;
  };

  // 重置变化检测
  const resetChangeDetection = () => {
    setHasChanges(false);
    setChangeCount(0);
    previousDataRef.current = data;
    acknowledgedDataRef.current = data;
  };

  return {
    hasChanges,
    changeCount,
    acknowledgeChanges,
    resetChangeDetection
  };
};

/**
 * 数组数据变化检测（检测新增/删除/修改）
 */
export interface ArrayChangeDetectorConfig<T> {
  data: T[];
  enabled?: boolean;
  keyExtractor: (item: T) => string | number;
  onItemAdded?: (items: T[]) => void;
  onItemRemoved?: (items: T[]) => void;
  onItemModified?: (items: T[]) => void;
}

export interface ArrayChangeDetectorReturn {
  hasChanges: boolean;
  addedCount: number;
  removedCount: number;
  modifiedCount: number;
  acknowledgeChanges: () => void;
  resetChangeDetection: () => void;
}

export const useArrayChangeDetector = <T,>({
  data,
  enabled = true,
  keyExtractor,
  onItemAdded,
  onItemRemoved,
  onItemModified
}: ArrayChangeDetectorConfig<T>): ArrayChangeDetectorReturn => {
  const [hasChanges, setHasChanges] = useState(false);
  const [addedCount, setAddedCount] = useState(0);
  const [removedCount, setRemovedCount] = useState(0);
  const [modifiedCount, setModifiedCount] = useState(0);

  const previousDataRef = useRef<Map<string | number, T>>(new Map());
  const acknowledgedDataRef = useRef<Map<string | number, T>>(new Map());

  useEffect(() => {
    if (!enabled) return;

    const currentMap = new Map(data.map(item => [keyExtractor(item), item]));
    const previousMap = previousDataRef.current;
    const acknowledgedMap = acknowledgedDataRef.current;

    // 检测新增
    const added: T[] = [];
    currentMap.forEach((item, key) => {
      if (!acknowledgedMap.has(key)) {
        added.push(item);
      }
    });

    // 检测删除
    const removed: T[] = [];
    acknowledgedMap.forEach((item, key) => {
      if (!currentMap.has(key)) {
        removed.push(item);
      }
    });

    // 检测修改
    const modified: T[] = [];
    currentMap.forEach((item, key) => {
      const acknowledgedItem = acknowledgedMap.get(key);
      if (acknowledgedItem && JSON.stringify(item) !== JSON.stringify(acknowledgedItem)) {
        modified.push(item);
      }
    });

    // 更新状态
    if (added.length > 0 || removed.length > 0 || modified.length > 0) {
      setHasChanges(true);
      setAddedCount(prev => prev + added.length);
      setRemovedCount(prev => prev + removed.length);
      setModifiedCount(prev => prev + modified.length);

      // 触发回调
      if (added.length > 0 && onItemAdded) onItemAdded(added);
      if (removed.length > 0 && onItemRemoved) onItemRemoved(removed);
      if (modified.length > 0 && onItemModified) onItemModified(modified);
    }

    previousDataRef.current = currentMap;
  }, [data, enabled, keyExtractor, onItemAdded, onItemRemoved, onItemModified]);

  // 确认变化
  const acknowledgeChanges = () => {
    setHasChanges(false);
    acknowledgedDataRef.current = new Map(data.map(item => [keyExtractor(item), item]));
  };

  // 重置变化检测
  const resetChangeDetection = () => {
    setHasChanges(false);
    setAddedCount(0);
    setRemovedCount(0);
    setModifiedCount(0);
    const currentMap = new Map(data.map(item => [keyExtractor(item), item]));
    previousDataRef.current = currentMap;
    acknowledgedDataRef.current = currentMap;
  };

  return {
    hasChanges,
    addedCount,
    removedCount,
    modifiedCount,
    acknowledgeChanges,
    resetChangeDetection
  };
};
